import { FC, MutableRefObject, useMemo, useRef } from 'react';
import { Box, StatusIconWithText } from '@alf/uikit';
import { useOtherSettings } from '@namespace/cms';
import { useI18n } from '@namespace/i18n';
import { PAYMENT_TYPES } from '../../../constants';
import { CANCEL_REASONS, STATUS_LABELS } from '../../../constants/history';
import { CancelReason, Status } from '../../../types/history';
import { TransactionStatusPopover } from '../TransactionStatusPopover';

const CANCEL_REASON_TITLES = {
  [CANCEL_REASONS.ADMINISTRATION]: 'web.balance.typeReason.admin',
  [CANCEL_REASONS.BANK]: 'web.balance.typeReason.bank',
  [CANCEL_REASONS.PLAYER]: 'web.balance.typeReason.player'
} as const;

interface StatusColumnProps {
  status: Status;
  cancelReason?: CancelReason;
  type: string;
}

export const StatusColumn: FC<StatusColumnProps> = ({
  status,
  cancelReason,
  type
}) => {
  const wrapperRef = useRef<HTMLDivElement>();

  const { payments } = useOtherSettings();
  const { supportServicePhone, isShowReasonOfWithdrawalCanceling } =
    payments ?? {};
  const { t } = useI18n();

  const title =
    CANCEL_REASON_TITLES[cancelReason as keyof typeof CANCEL_REASON_TITLES];

  const CANCEL_REASON_DESCRIPTIONS = useMemo(
    () => ({
      [CANCEL_REASONS.ADMINISTRATION]: [
        'web.balance.typeReason.adminDetails',
        [
          `<a href="tel:${supportServicePhone}"><strong>${supportServicePhone}</strong></a>`
        ]
      ],
      [CANCEL_REASONS.BANK]: ['web.balance.typeReason.bankDetails']
    }),
    [supportServicePhone]
  );

  const description =
    CANCEL_REASON_DESCRIPTIONS[
      cancelReason as keyof typeof CANCEL_REASON_DESCRIPTIONS
    ];

  const isWithraw = type === PAYMENT_TYPES.WITHDRAW;
  const shouldShowTransactionStatusPopover =
    title && isShowReasonOfWithdrawalCanceling && isWithraw;

  return (
    <Box ref={wrapperRef as MutableRefObject<HTMLDivElement>}>
      <StatusIconWithText label={t(STATUS_LABELS[status])} status={status} />

      {shouldShowTransactionStatusPopover && (
        <TransactionStatusPopover
          title={title}
          description={description}
          wrapperRef={wrapperRef as MutableRefObject<HTMLDivElement>}
        />
      )}
    </Box>
  );
};
